import { Plus } from "@assets"
import { router } from "@inertiajs/react"
import { useMemo, useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useAuth } from "@/hooks/use-auth"
import { useDragScroll } from "@/hooks/use-drag-scroll"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import { storiesNewPath, storyViewPath } from "@/routes"

import { StoryViewer } from "./story-viewer"

// Backend story interface (from Rails)
export interface BackendStory {
  id: string
  type: "story"
  created_at: string
  caption?: string
  media_type?: "image" | "video"
  media_url?: string
  expires_at: string
  views_count: number
  time_remaining_hours: number
  active: boolean
  author: {
    id: number
    name: string
    avatar_url?: string
    verified: boolean
  }
  viewed: boolean
  is_own_story: boolean
  url_path: string
}

// Frontend story interface (compatible with StoryViewer)
export interface Story {
  id: string
  userId: string
  userName: string
  userAvatar?: string
  items: StoryItem[]
  seen: boolean
  seenAt?: number
}

export interface StoryItem {
  id: string
  type: "image" | "video"
  url: string
  caption?: string
  duration?: number
  createdAt: string
  seen?: boolean
  seenAt?: number
}

// Transform backend stories to frontend format
function transformBackendStories(backendStories: BackendStory[]): Story[] {
  // Group stories by user
  const storiesByUser = backendStories.reduce(
    (acc, story) => {
      const odUserId = story.author.id.toString()
      if (!acc[odUserId]) {
        acc[odUserId] = {
          id: odUserId,
          userId: odUserId,
          userName: story.author.name,
          userAvatar: story.author.avatar_url,
          items: [],
          seen: true, // Will be updated based on items
          seenAt: undefined,
        }
      }

      // Add story as an item
      const item: StoryItem = {
        id: story.id,
        type: story.media_type === "video" ? "video" : "image",
        url: story.media_url ?? "",
        caption: story.caption,
        duration: 5000, // Default 5 seconds
        createdAt: story.created_at,
        seen: story.viewed,
        seenAt: story.viewed ? Date.now() : undefined,
      }

      acc[odUserId].items.push(item)

      // Update seen status
      if (!story.viewed) {
        acc[odUserId].seen = false
      }

      return acc
    },
    {} as Record<string, Story>,
  )

  // Sort items chronologically (oldest first) so new stories appear after existing ones
  const result = Object.values(storiesByUser)
  result.forEach((story) => {
    story.items.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
  })

  return result
}

interface StoriesProps {
  backendStories?: BackendStory[]
}

export function Stories({ backendStories = [] }: StoriesProps) {
  const { user, isAuthenticated } = useAuth()
  const { ref: storiesRef, onMouseDown: onStoriesMouseDown } = useDragScroll()
  const getInitials = useInitials()
  const [stories, setStories] = useState<Story[]>(() =>
    transformBackendStories(backendStories),
  )
  const [viewerOpen, setViewerOpen] = useState(false)
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0)
  const [initialItemIndex, setInitialItemIndex] = useState(0)
  // Orden fijo de IDs para el viewer (no cambia mientras está abierto)
  const [viewerOrder, setViewerOrder] = useState<string[]>([])

  // Separar historias del usuario logueado de las demás
  const userId = user?.id?.toString()
  const userStories = useMemo(() => {
    return userId ? stories.filter((s) => s.userId === userId) : []
  }, [stories, userId])

  // Ordenar historias de otros usuarios para la vista de miniaturas
  // No vistas primero, luego vistas ordenadas por última vista (más reciente al final)
  const sortedStories = useMemo(() => {
    const otherStories = userId
      ? stories.filter((s) => s.userId !== userId)
      : stories
    return [...otherStories].sort((a, b) => {
      const aFullySeen = a.items.every((item) => item.seen)
      const bFullySeen = b.items.every((item) => item.seen)

      // No vistas primero
      if (aFullySeen !== bFullySeen) {
        return aFullySeen ? 1 : -1
      }

      // Entre las vistas, ordenar por timestamp (última vista al final)
      if (aFullySeen && bFullySeen) {
        const aLastSeen = Math.max(...a.items.map((item) => item.seenAt ?? 0))
        const bLastSeen = Math.max(...b.items.map((item) => item.seenAt ?? 0))
        return aLastSeen - bLastSeen
      }

      return 0
    })
  }, [stories, userId])

  // Historias para el viewer (mantiene el orden fijo pero con datos actualizados)
  const viewerStories = useMemo(() => {
    if (viewerOrder.length === 0) return []
    return viewerOrder
      .map((id) => stories.find((s) => s.id === id))
      .filter((s): s is Story => s !== undefined)
  }, [viewerOrder, stories])

  const handleStoryClick = (storyId: string) => {
    const index = sortedStories.findIndex((s) => s.id === storyId)
    const story = sortedStories[index]
    if (!story) return
    // Encontrar el primer item no visto
    const firstUnseenIndex = story.items.findIndex((item) => !item.seen)
    // Fijar el orden de las historias para el viewer (no cambiará durante la visualización)
    setViewerOrder(sortedStories.map((s) => s.id))
    setCurrentStoryIndex(index)
    setInitialItemIndex(firstUnseenIndex >= 0 ? firstUnseenIndex : 0)
    setViewerOpen(true)
  }

  const handleUserStoryClick = () => {
    if (userStories.length > 0) {
      // Si tiene historias, abrir el viewer con sus historias
      const story = userStories[0]
      const firstUnseenIndex = story.items.findIndex((item) => !item.seen)
      setViewerOrder(userStories.map((s) => s.id))
      setCurrentStoryIndex(0)
      setInitialItemIndex(firstUnseenIndex >= 0 ? firstUnseenIndex : 0)
      setViewerOpen(true)
    } else {
      // Si no tiene historias, ir a crear
      router.visit(storiesNewPath())
    }
  }

  const handleStoryEnd = (storyId: string, itemId: string) => {
    // Actualizar estado local inmediatamente
    setStories((prev) =>
      prev.map((story) => {
        if (story.id !== storyId) return story

        const items = story.items.map((item) =>
          item.id === itemId
            ? { ...item, seen: true, seenAt: Date.now() }
            : item,
        )

        const allSeen = items.every((i) => i.seen)

        return {
          ...story,
          items,
          seen: allSeen,
          seenAt: allSeen ? Date.now() : story.seenAt,
        }
      }),
    )

    // Persistir en el backend
    fetch(storyViewPath(itemId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") ?? "",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          console.error(
            "Error marking story as viewed:",
            response.status,
            response.statusText,
          )
        }
      })
      .catch((error) => {
        console.error("Error marking story as viewed:", error)
      })
  }

  const handleViewerClose = () => {
    setViewerOpen(false)
  }

  const handleStoryDeleted = (storyId: string, itemId: string) => {
    // Actualizar el estado local para remover la historia eliminada
    setStories((prev) =>
      prev
        .map((story) => {
          if (story.id !== storyId) return story
          // Remover el item eliminado
          const updatedItems = story.items.filter((item) => item.id !== itemId)
          // Si no quedan items, retornar null para filtrar después
          if (updatedItems.length === 0) {
            return null
          }
          return { ...story, items: updatedItems }
        })
        .filter((story): story is Story => story !== null),
    )
  }

  const handleNextStory = () => {
    if (currentStoryIndex < viewerStories.length - 1) {
      setCurrentStoryIndex((prev) => prev + 1)
    } else {
      setViewerOpen(false)
    }
  }

  const handlePrevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex((prev) => prev - 1)
    }
  }

  return (
    <>
      <div
        ref={storiesRef}
        onMouseDown={onStoriesMouseDown}
        className="scrollbar-hide-x w-full cursor-grab overflow-x-auto select-none active:cursor-grabbing"
      >
        <div className="flex items-end gap-[6px] px-[15px] md:px-0">
          {/* Current user's story (Tu Clip) */}
          {isAuthenticated &&
            user &&
            (() => {
              const hasStories = userStories.length > 0
              return (
                <div className="flex flex-shrink-0 flex-col items-center gap-[4px]">
                  <div className="relative h-[78px] w-[78px]">
                    {/* Avatar button - abre historias o crea */}
                    <button
                      type="button"
                      className="h-full w-full cursor-pointer"
                      onClick={handleUserStoryClick}
                    >
                      <div
                        className={cn(
                          "flex h-[78px] w-[78px] items-center justify-center rounded-full p-[5px]",
                          hasStories ? "bg-muted" : "bg-transparent",
                        )}
                      >
                        {hasStories && userStories[0]?.items[0]?.url ? (
                          <img
                            src={userStories[0].items[0].url}
                            alt={user.name ?? ""}
                            className="border-background h-[68px] w-[68px] rounded-full border-2 object-cover"
                          />
                        ) : (
                          <Avatar className="border-background h-[68px] w-[68px] border-2">
                            <AvatarImage
                              src={user.avatar_url ?? undefined}
                              alt={user.name ?? ""}
                            />
                            <AvatarFallback className="text-primary gradient-avatar p-1 text-[24px] font-semibold tracking-[0.2px]">
                              {getInitials(user.name ?? "")}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    </button>
                    {/* Plus button - siempre crea nueva historia */}
                    <button
                      type="button"
                      className="bg-muted-foreground absolute right-[2px] bottom-[2px] flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[6px] p-0.5"
                      onClick={(e) => {
                        e.stopPropagation()
                        router.visit(storiesNewPath())
                      }}
                    >
                      <Plus className="h-full w-full text-white" />
                    </button>
                  </div>
                  <span className="font-roboto-condensed text-muted-foreground max-w-[60px] truncate text-[13px] leading-[15px] font-medium tracking-[0.13px]">
                    Tu Clip
                  </span>
                </div>
              )
            })()}

          {/* Other users' stories */}
          {sortedStories.map((story) => (
            <div
              key={story.id}
              className="flex flex-shrink-0 flex-col items-center gap-[4px]"
            >
              <button
                type="button"
                onClick={() => handleStoryClick(story.id)}
                className="relative cursor-pointer"
              >
                {(() => {
                  const allSeen = story.items.every((item) => item.seen)
                  return (
                    <div
                      className={cn(
                        "flex h-[78px] w-[78px] items-center justify-center rounded-full p-[5px]",
                        allSeen ? "bg-muted" : "bg-transparent",
                      )}
                      style={
                        allSeen
                          ? undefined
                          : {
                              background:
                                "linear-gradient(36deg, #C60B1E 0%, #FCBF49 40%, #75AADB 75%, #FFFFFF 100%)",
                              opacity: 1,
                            }
                      }
                    >
                      {story.items[0]?.url ? (
                        <img
                          src={story.items[0].url}
                          alt={story.userName ?? ""}
                          className="border-background h-[68px] w-[68px] rounded-full border-2 object-cover"
                        />
                      ) : (
                        <Avatar className="border-background h-[68px] w-[68px] border-2">
                          <AvatarImage
                            src={story.userAvatar ?? undefined}
                            alt={story.userName ?? ""}
                          />
                          <AvatarFallback className="text-primary gradient-avatar p-1 text-[24px] font-semibold tracking-[0.2px]">
                            {getInitials(story.userName ?? "")}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  )
                })()}
              </button>
              <span className="font-roboto-condensed text-muted-foreground line-clamp-1 h-[15px] max-w-[70px] text-center text-[13px] leading-[15px] font-medium tracking-[0.13px]">
                {story.userName}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Story Viewer Modal */}
      {viewerOpen && viewerStories.length > 0 && (
        <StoryViewer
          stories={viewerStories}
          currentIndex={currentStoryIndex}
          initialItemIndex={initialItemIndex}
          onClose={handleViewerClose}
          onNext={handleNextStory}
          onPrev={handlePrevStory}
          onStoryEnd={handleStoryEnd}
          onStoryDeleted={handleStoryDeleted}
          currentUserId={user?.id?.toString()}
        />
      )}
    </>
  )
}
